/*
 * Variant wheels (featured product, "Swipe wheels on mobile" setting).
 *
 * On phones each option row (color swatches, sizes) becomes a horizontal wheel:
 * swipe side to side, the option that settles in the middle is selected. Tapping an
 * option or using the arrow keys also works and scrolls it to the middle.
 *
 * Selection goes through Dawn's own radio inputs and change event, so variant
 * logic, price, availability and checkout are untouched. To keep the wheels where
 * the customer left them, this section updates option state in place instead of
 * letting Dawn rebuild the whole picker after each change.
 *
 * Speed: positions are measured only on load and resize; scrolling reads one
 * number and writes transforms once per frame.
 */
(() => {
  if (window.wodVariantWheels) return;
  window.wodVariantWheels = true;

  const PHONE = window.matchMedia('(max-width: 749px)');
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
  const SETTLE_DELAY = 140; // ms without scrolling before the middle option is selected
  const MAX_TURN = 55; // degrees an edge option is turned away
  const MIN_SCALE = 0.7;

  class Wheel {
    constructor(track) {
      this.track = track;
      this.items = [...track.querySelectorAll('label')];
      this.frame = 0;
      this.settleTimer = 0;
      this.target = null; // option the wheel is sliding to after a tap or arrow key

      this.onScroll = this.onScroll.bind(this);
      track.addEventListener('scroll', this.onScroll, { passive: true });
      if ('onscrollend' in window) track.addEventListener('scrollend', () => this.settle());

      // A finger or mouse wheel on the track hands control back to the customer.
      const takeOver = () => (this.target = null);
      track.addEventListener('pointerdown', takeOver, { passive: true });
      track.addEventListener('touchstart', takeOver, { passive: true });
      track.addEventListener('wheel', takeOver, { passive: true });

      // Tap: the label checks its radio natively; bring it to the middle.
      track.addEventListener('click', (event) => {
        const label = event.target.closest('label');
        if (label && this.active()) requestAnimationFrame(() => this.centerOn(this.items.indexOf(label), true));
      });

      // Keyboard arrows (or anything else) changing the radio: follow it.
      track.addEventListener('change', () => {
        if (this.active() && !this.selecting) this.centerChecked(true);
      });

      new ResizeObserver(() => this.refresh()).observe(track);
      this.refresh();
    }

    active() {
      return PHONE.matches && this.track.scrollWidth > this.track.clientWidth;
    }

    measure() {
      this.half = this.track.clientWidth / 2;
      this.centers = this.items.map((label) => label.offsetLeft + label.offsetWidth / 2);
      // Dawn's hidden radios are absolutely positioned, so they all sit at the start
      // of the track. Dawn refocuses the last chosen radio after every update, and the
      // browser would scroll the wheel back to the start to show it. Keeping each radio
      // under its own option means focusing it never moves the wheel.
      this.items.forEach((label, index) => {
        if (label.control) label.control.style.left = PHONE.matches ? `${Math.round(this.centers[index])}px` : '';
      });
    }

    refresh() {
      this.measure();
      if (this.active()) {
        this.centerChecked(false);
      } else {
        this.items.forEach((label) => (label.style.transform = ''));
      }
      this.render();
    }

    checkedIndex() {
      const index = this.items.findIndex((label) => label.control && label.control.checked);
      return index < 0 ? 0 : index;
    }

    centerOn(index, smooth) {
      if (index < 0 || !this.centers) return;
      if (smooth && this.target === index) return; // already on its way there
      const left = this.centers[index] - this.half;
      const max = this.track.scrollWidth - this.track.clientWidth;
      if (Math.abs(Math.max(0, Math.min(max, left)) - this.track.scrollLeft) < 1) {
        this.target = null; // already there, nothing will scroll
        return;
      }
      this.target = index;
      this.track.scrollTo({ left, behavior: smooth ? 'smooth' : 'auto' });
    }

    centerChecked(smooth) {
      this.centerOn(this.checkedIndex(), smooth);
    }

    nearestIndex() {
      const middle = this.track.scrollLeft + this.half;
      let best = 0;
      let bestDistance = Infinity;
      this.centers.forEach((center, index) => {
        const distance = Math.abs(center - middle);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = index;
        }
      });
      return best;
    }

    onScroll() {
      if (!this.frame) this.frame = requestAnimationFrame(() => this.render());
      clearTimeout(this.settleTimer);
      this.settleTimer = setTimeout(() => this.settle(), SETTLE_DELAY);
    }

    render() {
      this.frame = 0;
      if (!this.active()) return;
      const middle = this.track.scrollLeft + this.half;
      const turn = REDUCED_MOTION.matches ? 0 : MAX_TURN;
      this.items.forEach((label, index) => {
        const offset = Math.max(-1, Math.min(1, (this.centers[index] - middle) / this.half));
        const scale = 1 - Math.abs(offset) * (1 - MIN_SCALE);
        label.style.transform = `rotateY(${(-offset * turn).toFixed(1)}deg) scale(${scale.toFixed(3)})`;
      });
    }

    // Select whatever settled in the middle, through Dawn's normal change event.
    settle() {
      clearTimeout(this.settleTimer);
      if (!this.active() || this.syncing) return;
      const nearest = this.nearestIndex();
      // While sliding to a tapped option, ignore stops along the way.
      if (this.target !== null) {
        if (nearest === this.target) this.target = null;
        return;
      }
      const input = this.items[nearest]?.control;
      if (!input || input.checked || input.disabled) return;
      input.checked = true;
      this.selecting = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      this.selecting = false;
    }
  }

  // Update the existing option inputs from Dawn's fresh HTML instead of replacing
  // the picker, so wheel positions (and an in-progress swipe) survive.
  function syncOptionsInPlace(productInfo, wheels) {
    productInfo.updateOptionValues = (html) => {
      const fresh = html.querySelector('variant-selects');
      const current = productInfo.variantSelectors;
      if (!fresh || !current) return;
      wheels.forEach((wheel) => (wheel.syncing = true));

      fresh.querySelectorAll('input[type="radio"]').forEach((next) => {
        const input = [...current.querySelectorAll('input[type="radio"]')].find(
          (candidate) => candidate.name === next.name && candidate.value === next.value
        );
        if (!input) return;
        input.checked = next.checked;
        input.disabled = next.disabled;
        input.className = next.className;
        [...next.attributes].forEach((attribute) => {
          if (attribute.name.startsWith('data-')) input.setAttribute(attribute.name, attribute.value);
        });
        const nextLabel = next.nextElementSibling;
        const label = input.nextElementSibling;
        if (nextLabel && label && nextLabel.tagName === 'LABEL' && label.tagName === 'LABEL') {
          // Only touch what changed, so the wheel does not re-layout mid swipe.
          if (label.className !== nextLabel.className) label.className = nextLabel.className;
          if (label.innerHTML !== nextLabel.innerHTML) label.innerHTML = nextLabel.innerHTML;
        }
      });

      const nextValues = fresh.querySelectorAll('[data-selected-value]');
      current.querySelectorAll('[data-selected-value]').forEach((element, index) => {
        if (nextValues[index]) element.textContent = nextValues[index].textContent;
      });

      [...fresh.attributes].forEach((attribute) => {
        if (attribute.name.startsWith('data-')) current.setAttribute(attribute.name, attribute.value);
      });

      requestAnimationFrame(() =>
        wheels.forEach((wheel) => {
          wheel.syncing = false;
          wheel.render();
        })
      );
    };
  }

  function setup() {
    document.querySelectorAll('.featured-product--wheel').forEach((section) => {
      const productInfo = section.closest('product-info');
      if (!productInfo || productInfo.dataset.wheels) return;
      productInfo.dataset.wheels = 'true';
      const wheels = [...section.querySelectorAll('.product-form__options')].map((track) => new Wheel(track));
      syncOptionsInPlace(productInfo, wheels);
      PHONE.addEventListener('change', () => wheels.forEach((wheel) => wheel.refresh()));
    });
  }

  customElements.whenDefined('product-info').then(setup);
})();
